import { useBusyAction } from '@/db/useBusyAction';
import logger from '@/util/logger';
import { EquipmentTypeCandidateDao, EquipmentTypeDao } from '@rock/db-admin';
import type { EquipmentType, EquipmentTypeCandidateId, EquipmentTypeId } from '@rock/types';
import { toast } from 'sonner';

const log = logger('usePromoteCandidate');
const candidateDao = new EquipmentTypeCandidateDao();
const equipmentTypeDao = new EquipmentTypeDao();

/**
 * Promotion actions for one equipment-type candidate: point it at the catalog type meant to serve its
 * demand, either by seeding a brand-new draft from it or by linking a type the catalog already has.
 * The pointer (`promotionTypeId`) is what lets the candidate page link to that type, and later remap
 * the linked customs onto it. Nothing here publishes — the type goes live from its own type page,
 * where its fields, specs, and guide are edited.
 */
export function usePromoteCandidate(candidateId: EquipmentTypeCandidateId) {
    const { busy, run } = useBusyAction(log, { candidateId });

    return {
        busy,
        /** Seed a new catalog type as a draft and point the candidate at it. */
        promoteToNewType: (type: EquipmentType) =>
            run('Promote', async () => {
                await equipmentTypeDao.setEquipmentTypeDraft(type.id, type);
                await candidateDao.setPromotionTypeId(candidateId, type.id);
                toast.success('Draft type created', { description: 'Publish it from the type page when it’s ready.' });
                log.info('Seeded equipment type draft from candidate', { typeId: type.id, candidateId });
            }),
        /** Point the candidate at a type the catalog already has — no draft is written. */
        linkExistingType: (typeId: EquipmentTypeId) =>
            run('Link type', async () => {
                await candidateDao.setPromotionTypeId(candidateId, typeId);
                toast.success(`Linked to ${typeId}`);
                log.info('Linked candidate to existing type', { typeId, candidateId });
            }),
        /**
         * Drop the pointer — the linked type was discarded, deleted, or the wrong call. Only the
         * pointer goes: a draft type this candidate seeded stays in the catalog until it's discarded
         * from its own type page.
         */
        unlinkType: () =>
            run('Unlink type', async () => {
                await candidateDao.setPromotionTypeId(candidateId, undefined);
                toast.success('Unlinked', {
                    description: 'Any draft type it seeded stays — discard it from its type page.',
                });
                log.info('Unlinked candidate from its promotion type', { candidateId });
            }),
    };
}
